import React, { useState, useEffect } from "react";
import Select from "react-select";

export type Driver = {
  id?: number;
  nome: string;
  naturalidade: string;
  pais: string;
  documento: string;
  numeroCartaConducao: string;
  dataExpedicao: string;
  dataValidade: string;
  categoria: string;
  telefone: string;
  email: string;
  morada: string;
  registoCriminal: string;
  idiomas: string[];
  descricao: string;
  foto?: File | null;
};

type DriverFormProps = {
  initialData?: Driver | null;
  errors: Record<string, string>;
  onSubmit: (driver: Driver) => void;
  onCancel: () => void;
};

const LABELS: Record<keyof Driver, string> = {
  id: "ID",
  nome: "Name",
  naturalidade: "Place of Birth",
  pais: "Country",
  documento: "Document",
  numeroCartaConducao: "Driver License Number",
  dataExpedicao: "Issue Date",
  dataValidade: "Expiry Date",
  categoria: "Category",
  telefone: "Phone",
  email: "Email",
  morada: "Address",
  registoCriminal: "Criminal Record",
  idiomas: "Languages",
  descricao: "Description",
  foto: "Photo",
};

const languageOptions = [
  { value: "English", label: "English" },
  { value: "Italian", label: "Italian" },
  { value: "French", label: "French" },
  { value: "German", label: "German" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "Spanish", label: "Spanish" },
  { value: "Dutch", label: "Dutch" },
  { value: "Russian", label: "Russian" },
  { value: "Mandarin", label: "Mandarin" },
  { value: "Japanese", label: "Japanese" },
  // ...adicione o resto se quiser
];

const DriverForm: React.FC<DriverFormProps> = ({ initialData, errors, onSubmit, onCancel }) => {
  const emptyDriver: Driver = {
    nome: "",
    naturalidade: "",
    pais: "",
    documento: "",
    numeroCartaConducao: "",
    dataExpedicao: "",
    dataValidade: "",
    categoria: "",
    telefone: "",
    email: "",
    morada: "",
    registoCriminal: "",
    idiomas: [],
    descricao: "",
    foto: null,
  };

  const [driver, setDriver] = useState<Driver>(initialData || emptyDriver);
  const [hasCriminalRecord, setHasCriminalRecord] = useState(!!driver.registoCriminal);
  const [previewFoto, setPreviewFoto] = useState<string | null>(null);

  // 🔹 Atualiza o state sempre que inicialData muda (novo cadastro ou edição)
  useEffect(() => {
    if (initialData) {
      setDriver(initialData);
      setHasCriminalRecord(!!initialData.registoCriminal);

      if (initialData.foto instanceof File) {
        setPreviewFoto(URL.createObjectURL(initialData.foto));
      } else {
        setPreviewFoto(null);
      }
    }
  }, []);


  const handleChange = (field: keyof Driver, value: any) => {
    setDriver((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSubmit(driver);
  };

  return (
    <div className="card p-3 mb-4">
      <div className="row g-3">
        {/* Nome, Naturalidade, País, Documento */}
        {["nome", "naturalidade", "pais", "documento"].map((field) => (
          <div className="col-md-6" key={field}>
            <label className="form-label">{LABELS[field as keyof Driver]}</label>
            <input
              type="text"
              className={`form-control ${errors[field] ? "is-invalid" : ""}`}
              value={driver[field as keyof Driver] as string}
              onChange={(e) => handleChange(field as keyof Driver, e.target.value)}
            />
            {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
          </div>
        ))}

        {/* Número da Carta, Datas, Categoria */}
        {["numeroCartaConducao", "dataExpedicao", "dataValidade", "categoria"].map((field) => (
          <div className="col-md-6" key={field}>
            <label className="form-label">{LABELS[field as keyof Driver]}</label>
            <input
              type={field.includes("data") ? "date" : "text"}
              className={`form-control ${errors[field] ? "is-invalid" : ""}`}
              value={driver[field as keyof Driver] as string}
              onChange={(e) => handleChange(field as keyof Driver, e.target.value)}
            />
            {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
          </div>
        ))}

        {/* Telefone, Email, Morada */}
        {["telefone", "email", "morada"].map((field) => (
          <div className="col-md-6" key={field}>
            <label className="form-label">{LABELS[field as keyof Driver]}</label>
            <input
              type="text"
              className={`form-control ${errors[field] ? "is-invalid" : ""}`}
              value={driver[field as keyof Driver] as string}
              onChange={(e) => handleChange(field as keyof Driver, e.target.value)}
            />
            {errors[field] && <div className="invalid-feedback">{errors[field]}</div>}
          </div>
        ))}

        {/* Registo Criminal */}
        <div className="col-md-6">
          <label className="form-label">{LABELS.registoCriminal}</label>
          <div className="form-check mb-2">
            <input
              type="checkbox"
              className="form-check-input"
              id="new-registoCriminal-check"
              checked={hasCriminalRecord}
              onChange={(e) => {
                setHasCriminalRecord(e.target.checked);
                if (!e.target.checked) handleChange("registoCriminal", "");
              }}
            />
            <label className="form-check-label" htmlFor="new-registoCriminal-check">
              Yes
            </label>
          </div>

          {hasCriminalRecord && (
            <input
              type="text"
              className="form-control"
              placeholder="Enter criminal record details"
              value={driver.registoCriminal}
              onChange={(e) => handleChange("registoCriminal", e.target.value)}
            />
          )}
        </div>

        {/* Idiomas */}
        <div className="col-md-6">
          <label className="form-label">Languages Spoken</label>
          <Select
            isMulti
            options={languageOptions}
            value={driver.idiomas.map((idioma) => ({ label: idioma, value: idioma }))}
            onChange={(selected) =>
              handleChange(
                "idiomas",
                Array.isArray(selected) ? selected.map((s) => s.value) : []
              )
            }
          />
        </div>

        {/* Descrição */}
        <div className="col-md-12">
          <label className="form-label">{LABELS.descricao}</label>
          <textarea
            className="form-control"
            rows={3}
            value={driver.descricao}
            onChange={(e) => handleChange("descricao", e.target.value)}
          />
        </div>

        {/* Foto */}
        <div className="col-md-6">
          <label className="form-label">{LABELS.foto}</label>
          <input
            type="file"
            accept="image/*"
            className="form-control"
            onChange={(e) => {
              const file = e.target.files ? e.target.files[0] : null;
              handleChange("foto", file);
              if (file) setPreviewFoto(URL.createObjectURL(file));
              else setPreviewFoto(null);
            }}
          />

          {previewFoto && (
            <div className="mt-2">
              <p className="mb-1">Preview:</p>
              <img
                src={previewFoto}
                alt="Driver Preview"
                style={{ maxWidth: "150px", borderRadius: "8px" }}
              />
            </div>
          )}
        </div>

        <div className="alert alert-warning mb-3">
          ⚠️ Remember to assign a vehicle to the driver and set their working days!
        </div>

        {/* Buttons */}
        <div className="col-12 text-end">
          <button className="btn btn-secondary me-2" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-success" onClick={handleSubmit}>
            Save Driver
          </button>
        </div>
      </div>
    </div>
  );
};

export default DriverForm;